import { ChatChannelIds } from '@core/types';
import { TabGroup } from '@core/ui';
import { useAppSelector } from '@online-webinar/store';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { ChatChannelTabName } from '../../atoms/ChatChannelTabName';

export const ChatChannelsTabs = ({
    activeChannelId,
    onActiveChannelChange
}: {
    activeChannelId: string;
    onActiveChannelChange: (channelId: string) => void;
}) => {
    const [activeTab, setActiveTab] = useState<number>(0);
    const [joinedChannel, setJoinedChannel] = useState<string | null>(null);
    const allJoinedChannels = useAppSelector((state) => state.liveWebinar.joinedChannels);
    const hasSpeakers = useAppSelector((state) => state.liveWebinar.speakers.length > 0);
    const [previousJoinedChannels, setPreviousJoinedChannels] = useState<string[]>([]);

    // Without speakers nothing may hint that speakers can exist (spec 8e), so the speakers
    // channel tab only appears while the speaker roster is non-empty.
    const joinedChannels = useMemo(
        () => (hasSpeakers ? allJoinedChannels : allJoinedChannels.filter((channelId) => channelId !== ChatChannelIds.speakersChannel)),
        [allJoinedChannels, hasSpeakers]
    );

    useEffect(() => {
        if (activeChannelId) {
            if (joinedChannels.includes(activeChannelId)) {
                setActiveTab(joinedChannels.indexOf(activeChannelId));
            }
        }
    }, [activeChannelId, joinedChannels]);

    useEffect(() => {
        if (joinedChannels.length === 0) return;

        // Set the active tab to the last joined channel
        let tab = activeTab;
        if (activeTab >= joinedChannels.length) {
            tab = joinedChannels.length - 1;
            setActiveTab(tab);
        }

        // Call the onActiveChannelChange with the current active channel
        if (joinedChannel !== joinedChannels[tab]) {
            setJoinedChannel(joinedChannels[tab]);
            onActiveChannelChange(joinedChannels[tab]);
        }
    }, [joinedChannels, joinedChannel, activeTab, onActiveChannelChange]);

    useEffect(() => {
        // Deep compare joined channels to previous joined channels
        if (isEqual(joinedChannels, previousJoinedChannels)) {
            return;
        }

        if (activeTab >= joinedChannels.length) {
            setActiveTab(joinedChannels.length - 1);
        }

        // Find out which channel was added
        const addedChannel = joinedChannels.filter((channel) => !previousJoinedChannels.includes(channel));
        if (addedChannel.length > 0) {
            setActiveTab(joinedChannels.indexOf(addedChannel[0]));
        }

        setPreviousJoinedChannels(joinedChannels);
    }, [joinedChannels, previousJoinedChannels, activeTab]);

    return (
        <div className="row-start-1 col-start-1 flex-none">
            <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                <TabGroup.TabList numTabs={joinedChannels.length} activeTab={activeTab} setActiveTab={setActiveTab} classNames="flex-wrap bg-gray-200">
                    {joinedChannels.map((channelId, index) => (
                        <TabGroup.Tab
                            key={channelId + '-tab'}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            index={index}
                            className={`nav-tab ${ChatChannelIds.isSystemChannel(channelId) ? '' : 'nav-tab--has-close'}`}
                            activeClassName="nav-tab--active"
                        >
                            <ChatChannelTabName channelId={channelId} />
                        </TabGroup.Tab>
                    ))}
                </TabGroup.TabList>
            </TabGroup>
        </div>
    );
};
